/**
 * useSoloToggle - switch one of a combatant's Solo modes on or off (plan modes-rework; useStanceToggle before it).
 *
 * One state write (`activeStances`), so it syncs and undoes like any other change of state; the effects follow by
 * themselves, because every projection reads the effective sheet (`engine/roster/stancedSheet`). The log says which
 * Solo modes are on now and what they bring.
 *
 * @param {object} args - actor, updateCombatantState, addRollNarrative,
 *   capturePreActionSnapshot, pushHistory and historyTrack (the switch is one
 *   undoable step).
 * @returns {(modeId: string) => void}
 */
import { useCallback, useRef } from 'react';
import { infoLine } from './shellCombatant';
import { effectSentence } from '../engine/character/effectItems';
import { soloOn, toggleSolo } from '../engine/character/soloModes';

/**
 * The log's line for a switch: the Solo modes on now, in the sheet's order, and what they bring - nothing in Squad
 * Mode, where they stand down (D5), so the line never promises a bonus play will not give.
 */
export function soloLine(name, modes, on, squad = false) {
  const now = modes.filter((m) => on.includes(m.id));
  if (now.length === 0) return `${name} has no Solo mode on.`;
  if (squad) return `${name}: ${now.map((m) => m.name).join(', ')}, standing down in Squad Mode.`;
  const brings = now.flatMap((m) => m.effects.map(effectSentence));
  return `${name}: ${now.map((m) => m.name).join(', ')}${brings.length ? ` (${brings.join(', ')})` : ''}.`;
}

function withActive(state, active) {
  const { activeStances: _was, ...rest } = state;
  return active.length > 0 ? { ...rest, activeStances: active } : rest;
}

export default function useSoloToggle(args) {
  const live = useRef(args);
  live.current = args;
  return useCallback((modeId) => {
    const { actor, updateCombatantState, addRollNarrative, pushHistory, capturePreActionSnapshot, historyTrack } =
      live.current;
    if (!actor) return;
    const modes = actor.sheet.soloModes ?? [];
    const before = soloOn(modes, actor.state.activeStances);
    const after = toggleSolo(modes, before, modeId);
    if (after.join() === before.join()) return;
    // The steps every one-press action takes, so a shared undo puts the switch back too: the snapshot, the local
    // push, and the arming that records it on the shared stack once it renders (2026-10-24: unarmed, a session's
    // Undo never saw a switch).
    capturePreActionSnapshot?.();
    pushHistory?.();
    updateCombatantState(actor.id, (st) => withActive(st, toggleSolo(modes, st.activeStances, modeId)));
    if (historyTrack) historyTrack.current = true;
    addRollNarrative(infoLine(soloLine(actor.name || 'The combatant', modes, after, !!actor.state.killTeam?.on)));
  }, []);
}
